import type { LeadKpiTileData } from "./LeadKpiTile.tsx"

/**
 * The comparison block `GET /crm/leads/stats/` sends with a tile.
 *
 * It is data — how much the figure moved and against what — and says nothing
 * about how to draw it. Turning it into the tile's arrow, wording and colour is
 * this module's job, for the same reason glyph and tint live client-side.
 */
export interface LeadKpiCompare {
  value: number
  delta: number
  /** Null when the baseline was zero, which has no percentage answer. */
  delta_pct: number | null
  label: string
}

export function describeLeadKpiCompare(
  compare: LeadKpiCompare | null | undefined,
  { higherIsBetter = true }: { higherIsBetter?: boolean } = {}
): LeadKpiTileData["delta"] {
  if (!compare) return null

  const direction =
    compare.delta > 0 ? "up" : compare.delta < 0 ? "down" : "flat"

  // Which way it moved and whether that is good news are different questions.
  // More lost deals is a rise and bad; fewer is a fall and good. Colouring off
  // the arrow alone paints a spike in Closed-Lost green.
  const tone =
    direction === "flat"
      ? "flat"
      : (direction === "up") === higherIsBetter
        ? "good"
        : "bad"

  // A percentage off a zero baseline is not "0%" — it has no answer, so the
  // move is reported in whole leads instead of a ratio that would mislead.
  const text =
    compare.delta_pct === null
      ? `${Math.abs(compare.delta)}`
      : `${Math.abs(compare.delta_pct).toFixed(1)}%`

  return { direction, tone, text, label: `vs ${compare.label}` }
}
